import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import Barcode from "react-barcode";
import Loader from "../../../../components/Loader";
import PlaceHolderImage from "../../../../assets/images/placeholder_image.png";
import styles from "./InventoryLogsPDF.module.sass";
import cn from "classnames";
import { GENERATE_PICKLIST_BY_INVENTORY } from "../../../../API/Inventory";
import Icon from "../../../../components/Icon";

const InventoryLogsPDF = () => {
  const [data, setData] = useState([]);
  const [orders, setOrders] = useState([]);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const printComponentRef = useRef();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const checkedItems = location.state?.checkedItems || {};
  const itemIds = Object.keys(checkedItems);
  const { id } = useParams();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await GENERATE_PICKLIST_BY_INVENTORY(parseInt(id));
        setData(response.data || []);
      } catch (error) {
        console.error("Error fetching inventory logs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);
  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  useEffect(() => {
    // Hide the text element within the barcode
    const barcodes = document.querySelectorAll(".barcode");
    barcodes.forEach((barcode) => {
      const texts = barcode.querySelectorAll("text");
      texts.forEach((text) => (text.style.display = "none"));
    });
  }, [data]);

  const formatDateTime = (date) => {
    return moment(date).format("MM-DD-YYYY, hh:mm:ss A");
  };
  const formatName = (name) => {
    if (!name) return "";
    return name.split("_")[0].charAt(0).toUpperCase() + name.slice(1);
  };

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
    documentTitle: "Picklist",
    pageStyle: `
    @page {
      size: A4  ;
      margin: 0 !important; /* Force margin to 0 */
    }
    @media print {
      body {
        margin: 0 !important; /* Remove body margin */
        padding: 0 !important; /* Remove padding */
      }
      * {
        box-sizing: border-box; /* Ensure consistent box sizing */
      }
    }
  `,
    onAfterPrint: () => {
      console.log("Doc Downloaded");
    },
  });

  const findProductImage = (item) => {
    if (item.variant && item.variant.image_src) {
      return item.variant.image_src;
    } else {
      if (item.product?.ProductImage) {
        return item.product.ProductImage[0]?.src;
      }
      return null;
    }
  };
  const formatInventoryMode = (mode) => {
    switch (mode) {
      case "on_hand":
        return "On hand";
      case "available":
        return "Available";
      default:
        return mode;
    }
  };

  return (
    <div className={styles.scrollable_content}>
      <div className="flex flex-col items-center justify-center">
        <div className="flex  items-center justify-center">
          <button
            className={cn(
              "button",
              styles.button,
              "bg-blue-500 hover:bg-blue-700 text-white font-bold my-4 ml-3"
            )}
            onClick={handlePrint}
          >
            Print
          </button>
        </div>
        <div className="w-[80%] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader className="w-12 h-12" />
            </div>
          ) : (
            <div ref={printComponentRef}>
              {data.map((log) => (
                <div className={styles.page_break}>
                  <div key={log.id} className="mb-4 p-8 ">
                    <div className="pb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-xl font-bold mb-1">
                            Update Inventory Log
                          </h2>
                          <p className="text-gray-600">{log.id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[black] text-xl">
                            <strong>{log.store?.store_name}</strong>
                          </p>
                          <p className="text-[black] text-xl">
                            <strong>{log.variant_location?.name}</strong>
                          </p>
                          <p className={styles.subtext}>
                            {formatDateTime(log.created_at)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="border-t border-gray-300 mb-[10px]"></div>
                        <p className="text-sm">
                          <strong>User: </strong>
                          <span className={styles.subtext}>
                            {log.updated_by.full_name}
                          </span>{" "}
                          | <strong> Inventory mode: </strong>
                          <span className={styles.subtext}>
                            {formatInventoryMode(log.inventory_mode)}
                          </span>
                        </p>
                        <div className="border-t border-gray-300 mt-[10px]"></div>
                        <div className="border-t border-gray-300 mt-[20px]"></div>
                        <p className="text-sm mt-2">
                          <strong>Note: </strong>{" "}
                          <span className={styles.subtext}>
                            {log.notes ? log.notes : "-"}
                          </span>
                        </p>
                        <div className="border-t border-gray-300 mt-[10px]"></div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b border-gray-300">
                            <th className="border-b border-gray-300 p-[8px]">
                            </th>
                            <th className="border-b border-gray-300 p-[8px]">
                              Item
                            </th>
                            <th className="border-b border-gray-300 p-[8px]">
                              SKU
                            </th>
                            <th className="border-b border-gray-300 p-[8px]">
                              Barcode
                            </th>
                            <th className="border-b border-gray-300 p-[8px]">
                              Bin
                            </th>
                            <th className="border-b border-gray-300 p-2">
                              <div className="flex justify-center items-center gap-1.5">
                                From{" "}
                                <Icon
                                  name="arrow-right"
                                  size="16"
                                  fill="gray"
                                />{" "}
                                to
                              </div>
                            </th>
                            <th className="border-b border-gray-300 p-[8px]">
                              Updated by
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {log.ModifiedVariants?.map((item, index) => (
                            <tr
                              className="border-b border-gray-300"
                              key={index}
                            >
                              <td className="border-b border-gray-300 p-2 text-center content-center">
                                <img
                                  src={item.variant.image || PlaceHolderImage}
                                  alt=""
                                  style={{ margin: "auto" }}
                                  className="w-12 h-12"
                                />
                              </td>
                              <td className="border-b border-gray-300 p-2 text-center content-center">
                                {item.variant?.title || item.title || " "}
                              </td>
                              <td className="border-b border-gray-300 p-2 text-center content-center">
                                {item.variant?.sku || item.sku || " "}
                              </td>
                              <td className="border-b border-gray-300 p-2 text-center content-center">
                                {item.variant?.barcode}
                              </td>
                              <td className="border-b border-gray-300 p-2 text-center content-center">
                                {item.bin_location || "-"}
                              </td>
                              <td className="border-b border-gray-300 p-2 text-center content-center">
                                <div className="flex justify-center items-center gap-1.5">
                                  {item.from}
                                  <Icon
                                    name="arrow-right"
                                    size="16"
                                    fill="gray"
                                  />
                                  {item.to}
                                </div>
                              </td>
                              <td className="border-b border-gray-300 p-2 text-center content-center">
                                {item.quantity}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-8">
                      <div className="border-t border-gray-300"></div>
                      <div className="flex justify-between py-2 text-sm">
                        <div className="w-1/3 text-center">Summary</div>
                        <div className="w-1/3 text-center">
                          Total products: {log.products}
                        </div>
                        <div className="w-1/3 text-center">
                          Total items: {log.items}
                        </div>
                      </div>
                      <div className="border-t border-gray-300"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryLogsPDF;
